import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import merge from 'lodash/merge';
import sortBy from 'lodash/sortBy';
import { promiseAllObjectValues } from '@utils/promise.utils';
import cloneDeep from 'lodash/cloneDeep';
import { UTM_PARAMETERS } from '@/constants/utm-parameters';
import { i18n_t as $t } from '@/utils/i18n';
import { PRODUCT_LINES } from '@/settings/subscription-settings/product-lines';

import {
    fetchVisitorMetadata,
    fetchAccountMetadata,
    fetchParentAccountMetadata,
    fetchEventMetadata,
    removeSchema,
    updateSchema,
    updateRules,
    promoteSchema,
    updateViaEnabled,
    setObjectProperty,
    isHistoricalMetadata,
    iterateThroughMetadata,
    generateMetadataDescription,
} from './metadata.utils';
import { getEmailMetadataOptions } from '@pendo/services/EmailUtils';
import keyBy from 'lodash/keyBy';

export default {
    namespaced: true,
    state: {
        map: {},
        error: {},
        fetching: false,
        updating: false,
        removing: false,
        includesCardinality: false,
    },
    mutations: {
        setMap (state, { map }) {
            state.map = map;
        },
        setMapAtKey (state, { kind, metadata }) {
            state.map[kind] = metadata;
        },
        deleteMapKey (state, { kind }) {
            delete state.map[kind];
        },
        setUpdate (state, { kind, group, field, schema }) {
            if (!get(state.map[kind], group)) {
                state.map[kind][group] = {};
            }

            state.map[kind][group][field] = schema;
        },
        setRules (state, { kind, group, field, rules }) {
            state.map[kind][group][field].eventUrlRules = rules;
        },
        setObjectPropertySkipList (state, { kind, group, field, objectPropertySkipList }) {
            state.map[kind][group][field].objectPropertySkipList = objectPropertySkipList;
        },
        setObjectProperty (state, { kind, group, field, at }) {
            state.map[kind][group][field].objectProperty = at;
        },
        setDelete (state, { kind, group, field }) {
            delete state.map[kind][group][field];
        },
        setError (state, { error }) {
            state.error = error;
        },
        setFetching (state, { fetching }) {
            state.fetching = fetching;
        },
        setUpdating (state, { updating }) {
            state.updating = updating;
        },
        setRemoving (state, { removing }) {
            state.removing = removing;
        },
        setIncludesCardinality (state, { cardinality }) {
            state.includesCardinality = cardinality;
        },
    },
    actions: {
        async loadAll ({ commit, state, rootGetters }, { cardinality = false, reFetch = false } = {}) {
            const hasRequiredCardinality = !cardinality || cardinality === state.includesCardinality;
            if (!isEmpty(state.map) && !reFetch && hasRequiredCardinality) return Promise.resolve();

            commit('setFetching', { fetching: true });

            const includeParentAccounts = rootGetters['subscription/getFeatureFlag']('usesSubaccounts');

            const fetchPromises = {
                visitor: fetchVisitorMetadata({ cardinality }),
                account: fetchAccountMetadata({ cardinality }),
            };

            if (includeParentAccounts) {
                fetchPromises.parentAccount = fetchParentAccountMetadata({ cardinality });
            }

            // No cardinality includes all event metadata
            fetchPromises.eventNoCardinality = fetchEventMetadata({ cardinality: false });

            // Cardinality excludes event metadata with > 300 unique values, merge them together below
            if (cardinality) {
                fetchPromises.eventWithCardinality = fetchEventMetadata({ cardinality: true });
            }

            try {
                const result = await promiseAllObjectValues(fetchPromises);
                const { visitor, account, parentAccount, eventNoCardinality, eventWithCardinality } = result;
                const map = { visitor: visitor.data, account: account.data };
                if (parentAccount && includeParentAccounts) {
                    map.parentAccount = parentAccount.data;
                }

                // Cardinality excludes event metadata with > 300 unique values, merge them together
                if (eventNoCardinality) {
                    map.event = merge(eventNoCardinality.data, eventWithCardinality ? eventWithCardinality.data : {});
                }
                commit('setFetching', { fetching: false });
                commit('setIncludesCardinality', { cardinality });
                commit('setMap', { map });
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
        update ({ commit }, { schema = {} }) {
            const { kind, group, field, ...data } = schema;

            commit('setUpdating', { updating: true });

            return updateSchema({ kind, group, field }, data)
                .then(({ data }) => {
                    commit('setUpdating', { updating: false });
                    commit('setUpdate', { kind, group, field, schema: data });
                })
                .catch((error) => {
                    commit('setUpdating', { updating: false });
                    commit('setError', { error });
                    throw error;
                });
        },
        updateRules ({ commit }, { kind, group, field, rules, objectPropertySkipList }) {
            commit('setUpdating', { updating: true });

            const body = {};
            if (rules !== undefined) body.rules = rules;
            if (objectPropertySkipList !== undefined) body.objectPropertySkipList = objectPropertySkipList;

            return updateRules({ kind, group, field }, body)
                .then(({ data }) => {
                    commit('setUpdating', { updating: false });
                    // The endpoint returns the full schema, which uses `eventUrlRules` and omits
                    // empty lists. Key off what we submitted and fall back to it when the response
                    // omits the (now-empty) field, so the store reflects the save without a refetch.
                    if (rules !== undefined) {
                        commit('setRules', { kind, group, field, rules: data?.eventUrlRules ?? rules });
                    }
                    if (objectPropertySkipList !== undefined) {
                        commit('setObjectPropertySkipList', { kind, group, field, objectPropertySkipList: data?.objectPropertySkipList ?? objectPropertySkipList });
                    }
                })
                .catch((error) => {
                    commit('setUpdating', { updating: false });
                    commit('setError', { error });
                    throw error;
                });
        },
        setObjectProperty ({ commit }, { kind, group, field, at }) {
            commit('setUpdating', { updating: true });

            return setObjectProperty({ kind, group, field }, { at })
                .then(({ data }) => {
                    commit('setUpdating', { updating: false });
                    const resolvedAt = data && 'at' in data ? data.at : at;
                    commit('setObjectProperty', { kind, group, field, at: resolvedAt });
                })
                .catch((error) => {
                    commit('setUpdating', { updating: false });
                    commit('setError', { error });
                    throw error;
                });
        },
        updateViaEnabled ({ commit }, { schema = {} }) {
            const { kind, group, field } = schema;

            commit('setUpdating', { updating: true });

            return updateViaEnabled({ kind, group, field })
                .then(({ data }) => {
                    commit('setUpdating', { updating: false });
                    commit('setUpdate', { kind, group, field, schema: data });
                })
                .catch((error) => {
                    commit('setUpdating', { updating: false });
                    commit('setError', { error });
                    throw error;
                });
        },
        remove ({ commit }, { schema = {} }) {
            const { kind, group, field } = schema;

            commit('setRemoving', { removing: true });

            return removeSchema({ kind, group, field })
                .then(({ data }) => {
                    commit('setRemoving', { removing: false });
                    commit('setDelete', { kind, group, field, schema: data });
                })
                .catch((error) => {
                    commit('setRemoving', { removing: false });
                    commit('setError', { error });
                    throw error;
                });
        },
        async promote ({ commit }, { schema = {}, promote }) {
            const { kind, group, field } = schema;
            commit('setUpdating', { updating: true });

            let response;
            try {
                response = await promoteSchema({ kind, group, field }, { promote });
            } catch (error) {
                commit('setUpdating', { updating: false });
                commit('setError', { error });
                throw error;
            }
            commit('setUpdating', { updating: false });
            commit('setUpdate', { kind, group, field, schema: response.data });
        },
        async generateDescription ({ commit }, { kind, group, field, displayName, type, sample, description }) {
            commit('setUpdating', { updating: true });

            try {
                const response = await generateMetadataDescription({
                    kind,
                    group,
                    field,
                    displayName,
                    type,
                    sample,
                    description,
                });
                commit('setUpdating', { updating: false });

                return response.data;
            } catch (error) {
                commit('setUpdating', { updating: false });
                commit('setError', { error });
                throw error;
            }
        },
    },
    getters: {
        metadataListByKind: (state) => (kind, options = {}) => {
            const kindAdjusted = options.isUtm ? 'utm' : kind;

            return iterateThroughMetadata(get(state.map, kind, []), kindAdjusted).filter(({ group }) => {
                const { groupsToExclude } = options;
                if (groupsToExclude && !isEmpty(groupsToExclude)) {
                    return !groupsToExclude.includes(group);
                }

                return true;
            });
        },
        metadataList: (state, getters, rootState, rootGetters) => {
            const includeParentAccounts = rootGetters['subscription/getFeatureFlag']('usesSubaccounts');

            const iterateArray = [
                ...iterateThroughMetadata(state.map.visitor, 'visitor'),
                ...iterateThroughMetadata(state.map.account, 'account'),
            ];
            iterateArray.push(...iterateThroughMetadata(state.map.event, 'event'));

            if (includeParentAccounts) {
                iterateArray.push(...iterateThroughMetadata(state.map.parentAccount, 'parentAccount'));
            }

            return iterateArray;
        },
        historicalMetadata: (state, getters) => {
            const kindArr = ['visitor', 'account', 'parentAccount'];
            const metadataArray = [];
            kindArr.forEach((kind) => metadataArray.push(...getters.metadataListByKind(kind)));
            const historicalMetadata = metadataArray.filter(isHistoricalMetadata);

            return historicalMetadata;
        },
        visitorMetadataOptions: (state, getters, _rootState, rootGetters) => {
            const showAccounts = rootGetters['productLines/hasProductLinePurchased'](PRODUCT_LINES.CORE, 'accountsUIEnabled');
            const visitorMetadata = getters.metadataListByKind('visitor', { groupsToExclude: ['auto', 'pendo'] });
            const availableVisitorMetadata = visitorMetadata.filter((singleField) => {
                const typesToDisplay = ['boolean', 'string', 'integer'];
                const hideAccountFields = !showAccounts && (singleField.field.includes('account'));

                return typesToDisplay.includes(singleField.Type) && !hideAccountFields;
            });
            const visitorMetaDataWithViewByDisplayName = availableVisitorMetadata.map(({ DisplayName, ...rest }) => ({
                ...rest,
                displayName: DisplayName,
            }));

            return sortBy(visitorMetaDataWithViewByDisplayName, [({ displayName }) => displayName.toLowerCase()]);
        },
        sessionProperties: (state, getters, _rootState, rootGetters) => {
            if (!rootGetters['productLines/hasProductLinePurchased'](PRODUCT_LINES.ANALYTICS, 'webAnalyticsEnabled')) {
                return [];
            }
            const utmParameters = UTM_PARAMETERS();
            const sessionPropertiesArray = cloneDeep(getters.metadataListByKind('event', { isUtm: true })).filter(
                (item) => item.group === 'utm',
            );

            const channelSessionProperty = sessionPropertiesArray.filter((item) => {
                return item.field === 'channel';
            });
            if (channelSessionProperty.length > 0) {
                channelSessionProperty[0].DisplayName = $t('utmParameters.Channel');
            }

            let referrerSessionProperty = sessionPropertiesArray.filter((item) => {
                return item.field === 'referrer';
            });

            // We do not send a referrer if it is "direct", but user may want to filter on it
            if (isEmpty(referrerSessionProperty)) {
                referrerSessionProperty = [
                    {
                        field: 'referrer',
                        group: 'utm',
                        kind: 'utm',
                        Type: 'string',
                        ElementType: '',
                        uniqueValues: [],
                    },
                ];
            }
            if (referrerSessionProperty.length > 0) {
                referrerSessionProperty[0].DisplayName = $t('utmParameters.Referrer');
            }

            const automaticSessionProperties = [...channelSessionProperty, ...referrerSessionProperty];
            const utmParametersByKind = keyBy(utmParameters, 'field');
            const kindMap = new Map(sessionPropertiesArray.map((item) => [item.field, item]));
            const sortedUtmSessionProperties = Object.keys(utmParametersByKind)
                .filter((kind) => kindMap.has(kind))
                .map((kind) => {
                    return {
                        ...kindMap.get(kind),
                        DisplayName: utmParametersByKind[kind].DisplayName,
                    };
                });

            return [...automaticSessionProperties, ...sortedUtmSessionProperties];
        },
        accountMetadataOptions: (state, getters, _rootState, rootGetters) => {
            const showAccounts = rootGetters['productLines/hasProductLinePurchased'](PRODUCT_LINES.CORE, 'accountsUIEnabled');
            if (!showAccounts) {
                return [];
            }
            const typesToDisplay = ['boolean', 'string', 'integer'];
            const meta = getters.metadataListByKind('account', { groupsToExclude: ['pendo'] }).filter((metadata) => {
                return typesToDisplay.includes(metadata.Type);
            });

            return meta.map((el) => {
                return {
                    id: `${el.kind}.${el.group}.${el.field}`,
                    DisplayName: el.DisplayName,
                    tooltip: el.DisplayName,
                    group: el.group,
                    Type: el.Type,
                    field: el.field,
                    kind: el.kind,
                };
            });
        },
        visitorMetadataOptionsWithAutoGroup: (state, getters, _rootState, rootGetters) => {
            const showAccounts = rootGetters['productLines/hasProductLinePurchased'](PRODUCT_LINES.CORE, 'accountsUIEnabled');
            const typesToDisplay = ['boolean', 'string', 'integer'];
            const meta = getters.metadataListByKind('visitor', { groupsToExclude: ['pendo'] }).filter((metadata) => {
                const hideAccountFields = !showAccounts && (metadata.field.includes('account'));

                return typesToDisplay.includes(metadata.Type) && !hideAccountFields;
            });

            return meta.map((el) => {
                return {
                    id: `${el.kind}.${el.group}.${el.field}`,
                    DisplayName: el.DisplayName,
                    tooltip: el.DisplayName,
                    group: el.group,
                    Type: el.Type,
                    field: el.field,
                    kind: el.kind,
                };
            });
        },
        parentAccountMetadataOptions: (state, getters, _rootState, rootGetters) => {
            const showAccounts = rootGetters['productLines/hasProductLinePurchased'](PRODUCT_LINES.CORE, 'accountsUIEnabled');
            if (!showAccounts) {
                return [];
            }
            const typesToDisplay = ['boolean', 'string', 'integer'];
            const meta = getters
                .metadataListByKind('parentAccount', { groupsToExclude: ['pendo'] })
                .filter((metadata) => {
                    return typesToDisplay.includes(metadata.Type);
                });

            return meta.map((el) => {
                return {
                    id: `${el.kind}.${el.group}.${el.field}`,
                    DisplayName: el.DisplayName,
                    tooltip: el.DisplayName,
                    group: el.group,
                    Type: el.Type,
                    field: el.field,
                    kind: el.kind,
                };
            });
        },
        emailMetadataOptions: (state) => getEmailMetadataOptions(state.map, {
            visitor: $t('metadata.Visitor'),
            account: $t('metadata.Account'),
        }),
    },
};
